import {
  bulkUpsertBoPreferences,
  fetchBoPreferences,
  upsertBoPreference,
} from "@/api/user-preferences";
import {
  BO_PREFS_CACHE_PREFIX,
  isBoPreferenceLogicalKey,
} from "./constants";

const SYNC_DEBOUNCE_MS = 800;

let activeInstanceSlug: string | null = null;
let hydratePromise: Promise<void> | null = null;
/** Server sync is disabled until hydrate finishes — prevents mount-time writes from overwriting DB prefs. */
let serverSyncEnabled = false;
const pendingSync = new Map<string, ReturnType<typeof setTimeout>>();
const pendingValues = new Map<string, string>();

function cacheKey(logicalKey: string): string {
  return `${BO_PREFS_CACHE_PREFIX}${logicalKey}`;
}

function serializeForStorage(value: unknown): string {
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

function parseStoredValue(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

function clearPendingSyncState(): void {
  for (const timer of pendingSync.values()) {
    clearTimeout(timer);
  }
  pendingSync.clear();
  pendingValues.clear();
}

export function getActiveBoInstanceSlug(): string | null {
  return activeInstanceSlug;
}

export function areBoPreferencesReady(): boolean {
  return serverSyncEnabled;
}

export function readBoPreferenceRaw(logicalKey: string): string | null {
  return localStorage.getItem(cacheKey(logicalKey));
}

export function readBoPreference<T = unknown>(logicalKey: string): T | undefined {
  const raw = readBoPreferenceRaw(logicalKey);
  if (raw == null) return undefined;
  return parseStoredValue(raw) as T;
}

export function writeBoPreferenceLocal(
  logicalKey: string,
  value: unknown,
): void {
  const serialized = serializeForStorage(value);
  if (value === undefined) {
    localStorage.removeItem(cacheKey(logicalKey));
    pendingValues.delete(logicalKey);
    return;
  }
  localStorage.setItem(cacheKey(logicalKey), serialized);
  pendingValues.set(logicalKey, serialized);
}

function scheduleSyncToServer(logicalKey: string): void {
  if (
    !serverSyncEnabled ||
    !activeInstanceSlug ||
    !isBoPreferenceLogicalKey(logicalKey)
  ) {
    return;
  }

  const existing = pendingSync.get(logicalKey);
  if (existing) clearTimeout(existing);

  pendingSync.set(
    logicalKey,
    setTimeout(() => {
      pendingSync.delete(logicalKey);
      const raw = pendingValues.get(logicalKey);
      const config =
        raw === undefined ? null : parseStoredValue(raw ?? "");
      void upsertBoPreference(logicalKey, config).catch((err) => {
        console.error("[user-preferences] sync failed for", logicalKey, err);
      });
    }, SYNC_DEBOUNCE_MS),
  );
}

export function persistBoPreference(logicalKey: string, value: unknown): void {
  writeBoPreferenceLocal(logicalKey, value);
  scheduleSyncToServer(logicalKey);
}

export function removeBoPreference(logicalKey: string): void {
  localStorage.removeItem(cacheKey(logicalKey));
  pendingValues.delete(logicalKey);
  const existing = pendingSync.get(logicalKey);
  if (existing) clearTimeout(existing);
  pendingSync.delete(logicalKey);
  if (serverSyncEnabled && activeInstanceSlug) {
    void upsertBoPreference(logicalKey, null).catch((err) => {
      console.error("[user-preferences] remove sync failed for", logicalKey, err);
    });
  }
}

function configToStorageString(config: unknown): string {
  if (config === null || config === undefined) return "";
  if (typeof config === "string") return config;
  return JSON.stringify(config);
}

export async function hydrateBoPreferencesFromServer(
  instanceSlug: string,
): Promise<void> {
  if (hydratePromise) {
    await hydratePromise;
    if (activeInstanceSlug === instanceSlug && serverSyncEnabled) return;
  }

  activeInstanceSlug = instanceSlug;
  serverSyncEnabled = false;

  hydratePromise = (async () => {
    try {
      const { items } = await fetchBoPreferences();
      for (const item of items) {
        const serialized = configToStorageString(item.config);
        if (serialized === "") {
          localStorage.removeItem(cacheKey(item.logicalKey));
        } else {
          localStorage.setItem(cacheKey(item.logicalKey), serialized);
        }
      }
    } catch (err) {
      console.error("[user-preferences] hydrate failed", err);
    } finally {
      // Drop any mount-time local writes that raced with hydrate.
      clearPendingSyncState();
      serverSyncEnabled = true;
      hydratePromise = null;
    }
  })();

  await hydratePromise;
}

/**
 * Pushes current local BO preference keys to the server (one-shot migration).
 */
export async function migrateLocalBoPreferencesToServer(
  instanceSlug: string,
): Promise<void> {
  const items: { logicalKey: string; config: unknown }[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || key.startsWith(BO_PREFS_CACHE_PREFIX)) continue;
    if (!isBoPreferenceLogicalKey(key)) continue;

    const raw = localStorage.getItem(key);
    if (raw == null) continue;

    items.push({
      logicalKey: key,
      config: parseStoredValue(raw),
    });
    localStorage.setItem(cacheKey(key), raw);
    localStorage.removeItem(key);
  }

  if (items.length > 0) {
    await bulkUpsertBoPreferences(items);
  }

  activeInstanceSlug = instanceSlug;
}

export function clearBoPreferencesCache(): void {
  clearPendingSyncState();
  activeInstanceSlug = null;
  hydratePromise = null;
  serverSyncEnabled = false;

  const toRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(BO_PREFS_CACHE_PREFIX)) toRemove.push(key);
  }
  for (const key of toRemove) localStorage.removeItem(key);
}
